/**
 * HotelbedsHeaderSearch — Phase 5 (2026-07-27).
 *
 * Header-launched destination-lookup modal. Replaces the deprecated
 * Google-Places-backed `SearchModal` for the AppShell magnifying-glass
 * button. Same overall shape (navy title bar over a cream sheet) so
 * users don't perceive an app-wide UI shift — only the data source is
 * different.
 *
 * • Autocomplete: `GET /api/hotels/destinations/search?q=…`
 * • Pick a destination → nav to `/hotels/search?destinationCode=…`
 *   with default dates (today+30 → today+34) + 2 adults. The
 *   dedicated `/hotels` page still exists for users who want to fine-
 *   tune dates/guests before searching.
 */
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Search, X, MapPin, BedDouble } from "lucide-react";
import { api } from "@/lib/api";

function todayPlus(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function defaultSearchParams(destCode, destName) {
  const occ = {
    rooms: 1,
    adults: 2,
    children: 0,
    paxes: [
      { type: "AD", age: 30 },
      { type: "AD", age: 30 },
    ],
  };
  return new URLSearchParams({
    destinationCode: destCode,
    destinationName: destName || "",
    checkIn: todayPlus(30),
    checkOut: todayPlus(34),
    occupancies: JSON.stringify([occ]),
  }).toString();
}

export default function HotelbedsHeaderSearch({ open, onClose }) {
  const nav = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQ("");
      setSuggestions([]);
      // Preload the top-8 starter list so the sheet feels alive before
      // the user types anything.
      api.get("/hotels/destinations/search?q=&limit=8")
        .then(({ data }) => setSuggestions(data || []))
        .catch(() => setSuggestions([]));
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (!term) {
      // Reload the starter list on clear so we don't leave a stale
      // filtered result set behind.
      api.get("/hotels/destinations/search?q=&limit=8")
        .then(({ data }) => setSuggestions(data || []))
        .catch(() => setSuggestions([]));
      return;
    }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await api.get(
          `/hotels/destinations/search?q=${encodeURIComponent(term)}&limit=12`,
          { signal: ctrl.signal },
        );
        setSuggestions(data || []);
      } catch (err) {
        if (err.name !== "CanceledError" && err.code !== "ERR_CANCELED") {
          // eslint-disable-next-line no-console
          console.error("[header-search] failed:", err);
        }
      } finally { setBusy(false); }
    }, 220);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [q, open]);

  const pick = (dest) => {
    if (!dest?.code) return;
    onClose?.();
    // Phase 5 issue-fix (2026-07-27): stash origin path so the
    // results page's back button returns to the ORIGIN page
    // (lounge / messages / etc.), not a stale /hotels fallback.
    nav(`/hotels/search?${defaultSearchParams(dest.code, dest.name)}`, {
      state: { from: location.pathname + location.search },
    });
  };

  if (!open) return null;

  return (
    <div
      data-testid="header-search-modal"
      className="fixed inset-0 z-50 bg-tb-navy-deep/70 flex items-start justify-center px-3 pt-20"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="bg-white rounded-2xl border-2 border-tb-navy max-w-lg w-full overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-tb-navy-deep text-tb-gold-bright px-4 py-3 flex items-center gap-2 rounded-t-2xl">
          <BedDouble className="w-5 h-5" />
          <span className="font-hand text-lg flex-1">
            Search hotels
          </span>
          <button
            onClick={onClose}
            aria-label="Close"
            data-testid="header-search-close"
            className="font-hand text-tb-gold tb-tap"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-4 pt-4 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tb-navy/50" />
            <input
              ref={inputRef}
              type="search"
              data-testid="header-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && suggestions[0]) pick(suggestions[0]);
              }}
              placeholder="City or country — e.g. Paris, Madrid, Miami"
              className="w-full pl-9 pr-3 py-2.5 rounded-full bg-white border-2 border-tb-navy/15 focus:border-tb-navy outline-none font-hand text-tb-navy text-base placeholder:text-tb-navy/40"
            />
          </div>
        </div>
        <div className="max-h-[55vh] overflow-y-auto px-2 pb-4">
          {busy && (
            <p className="font-hand text-tb-navy/55 italic text-center py-3">searching…</p>
          )}
          {!busy && suggestions.length === 0 && q.trim() && (
            <p
              data-testid="header-search-empty"
              className="font-hand text-tb-navy/55 italic text-center py-6"
            >
              No destinations matched &ldquo;{q.trim()}&rdquo; — try a broader city or country.
            </p>
          )}
          {suggestions.length > 0 && (
            <ul data-testid="header-search-results" className="space-y-1">
              {suggestions.map((s) => (
                <li key={s.code}>
                  <button
                    type="button"
                    data-testid={`header-search-result-${s.code}`}
                    onClick={() => pick(s)}
                    className="w-full text-left rounded-xl hover:bg-tb-cream-2 px-3 py-2.5 flex items-center gap-3 tb-tap"
                  >
                    <div className="w-10 h-10 rounded-full bg-tb-navy/5 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-4 h-4 text-tb-navy/60" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-serif-it italic text-tb-navy text-lg leading-tight truncate">
                        {s.name}
                      </div>
                      <div className="font-hand text-tb-navy/55 text-xs truncate">
                        {s.country || s.countryCode}
                      </div>
                    </div>
                    <span className="text-tb-navy/40 text-[11px] uppercase tracking-widest font-hand">
                      {s.code}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="px-4 pb-4 pt-1 border-t border-tb-cream-3">
          <p className="font-hand text-tb-navy/50 text-xs italic text-center">
            Pick a destination and we&apos;ll open live Stay22 availability.
          </p>
        </div>
      </div>
    </div>
  );
}
